import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { brand } from "../mock/content";
import { getActiveLang, isBot } from "../lib/activeLang";
import { getConsent, saveConsent, OPEN_EVENT } from "../lib/consent";

const TXT = {
  it: { t: "Questo sito usa i cookie", p: "Usiamo cookie tecnici necessari al funzionamento e, solo con il tuo consenso, cookie statistici (Google Analytics) per migliorare il sito.", policy: "Cookie Policy", accept: "Accetta tutti", reject: "Rifiuta", custom: "Personalizza", save: "Salva preferenze", nec: "Necessari", necD: "Sempre attivi: lingua, carrello, sessione.", an: "Statistici", anD: "Misurano visite e uso del sito in forma aggregata." },
  en: { t: "This website uses cookies", p: "We use technical cookies required for the site to work and, only with your consent, analytics cookies (Google Analytics) to improve the site.", policy: "Cookie Policy", accept: "Accept all", reject: "Reject", custom: "Customize", save: "Save preferences", nec: "Necessary", necD: "Always active: language, cart, session.", an: "Analytics", anD: "Measure visits and site usage in aggregate form." },
  de: { t: "Diese Website verwendet Cookies", p: "Wir verwenden technisch notwendige Cookies und, nur mit Ihrer Einwilligung, Statistik-Cookies (Google Analytics), um die Website zu verbessern.", policy: "Cookie-Richtlinie", accept: "Alle akzeptieren", reject: "Ablehnen", custom: "Anpassen", save: "Auswahl speichern", nec: "Notwendig", necD: "Immer aktiv: Sprache, Warenkorb, Sitzung.", an: "Statistik", anD: "Messen Besuche und Nutzung in aggregierter Form." },
  fr: { t: "Ce site utilise des cookies", p: "Nous utilisons des cookies techniques nécessaires et, uniquement avec votre consentement, des cookies statistiques (Google Analytics) pour améliorer le site.", policy: "Politique cookies", accept: "Tout accepter", reject: "Refuser", custom: "Personnaliser", save: "Enregistrer", nec: "Nécessaires", necD: "Toujours actifs : langue, panier, session.", an: "Statistiques", anD: "Mesurent les visites et l'usage du site de façon agrégée." },
};

const btn = "h-10 px-5 text-[11px] uppercase tracking-[0.14em] font-bold transition-colors whitespace-nowrap";

const Toggle = ({ on, onChange, disabled, testid }) => (
  <button type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange?.(!on)} data-testid={testid}
    className={`relative w-10 h-5 rounded-full shrink-0 transition-colors ${disabled ? "opacity-60" : ""}`} style={{ background: on ? brand.yellow : "rgba(255,255,255,0.2)" }}>
    <span className="absolute left-0 top-0.5 h-4 w-4 rounded-full bg-black transition-transform" style={{ transform: on ? "translateX(22px)" : "translateX(2px)" }} />
  </button>
);

export default function CookieBanner() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => !isBot() && !getConsent());
  const [custom, setCustom] = useState(false);
  const [analytics, setAnalytics] = useState(() => !!getConsent()?.analytics);
  const L = TXT[getActiveLang()] || TXT.it;

  useEffect(() => {
    const show = () => { setAnalytics(!!getConsent()?.analytics); setCustom(true); setOpen(true); };
    window.addEventListener(OPEN_EVENT, show);
    return () => window.removeEventListener(OPEN_EVENT, show);
  }, []);

  const visible = open && !pathname.startsWith("/admin");
  useEffect(() => {
    document.body.toggleAttribute("data-cookie-open", visible);
    return () => document.body.removeAttribute("data-cookie-open");
  }, [visible]);

  if (!visible) return null;
  const done = (a) => { saveConsent(a); setOpen(false); setCustom(false); };
  const policyHref = `${getActiveLang() === "it" ? "" : `/${getActiveLang()}`}/legali/cookie-policy`;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[90] p-3 md:p-5 pointer-events-none" data-testid="cookie-banner">
      <div className="relative pointer-events-auto max-w-[1440px] mx-auto border-t-2 bg-[#0d0d0d]/95 backdrop-blur-md text-white shadow-[0_-10px_40px_rgba(0,0,0,0.5)] px-5 py-4 md:px-7 md:py-5" style={{ borderColor: brand.yellow }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
          <div className="flex gap-3 flex-1 min-w-0">
            <Cookie size={20} className="shrink-0 mt-0.5" style={{ color: brand.yellow }} />
            <div className="min-w-0">
              <p className="font-bold text-sm" style={{ fontFamily: "Space Grotesk" }}>{L.t}</p>
              <p className="mt-1 text-white/65 text-xs md:text-[13px] leading-relaxed" style={{ fontFamily: "Inter" }}>
                {L.p} <a href={policyHref} className="underline underline-offset-2 hover:text-white" style={{ color: brand.yellow }} data-testid="cookie-policy-link">{L.policy}</a>
              </p>
            </div>
          </div>
          {!custom && (
            <div className="grid grid-cols-2 sm:flex gap-2 shrink-0" style={{ fontFamily: "Space Grotesk" }}>
              <button onClick={() => setCustom(true)} className={`${btn} col-span-2 sm:col-span-1 text-white/70 hover:text-white`} data-testid="cookie-customize">{L.custom}</button>
              <button onClick={() => done(false)} className={`${btn} border border-white/30 hover:border-white`} data-testid="cookie-reject">{L.reject}</button>
              <button onClick={() => done(true)} className={`${btn} text-black hover:brightness-95`} style={{ background: brand.yellow }} data-testid="cookie-accept">{L.accept}</button>
            </div>
          )}
          {custom && (
            <button onClick={() => (getConsent() ? setOpen(false) : setCustom(false))} aria-label="Chiudi" className="absolute right-6 top-5 lg:hidden text-white/50" data-testid="cookie-close"><X size={18} /></button>
          )}
        </div>
        {custom && (
          <div className="mt-4 pt-4 border-t border-white/10 flex flex-col lg:flex-row lg:items-end gap-4" data-testid="cookie-prefs">
            <div className="flex-1 grid sm:grid-cols-2 gap-3" style={{ fontFamily: "Inter" }}>
              <div className="flex items-start gap-3 border border-white/10 p-3">
                <Toggle on disabled testid="cookie-toggle-necessary" />
                <div><p className="text-sm font-semibold">{L.nec}</p><p className="text-xs text-white/50">{L.necD}</p></div>
              </div>
              <div className="flex items-start gap-3 border border-white/10 p-3">
                <Toggle on={analytics} onChange={setAnalytics} testid="cookie-toggle-analytics" />
                <div><p className="text-sm font-semibold">{L.an}</p><p className="text-xs text-white/50">{L.anD}</p></div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:flex gap-2 shrink-0" style={{ fontFamily: "Space Grotesk" }}>
              <button onClick={() => done(false)} className={`${btn} border border-white/30 hover:border-white`} data-testid="cookie-prefs-reject">{L.reject}</button>
              <button onClick={() => done(analytics)} className={`${btn} text-black hover:brightness-95`} style={{ background: brand.yellow }} data-testid="cookie-save">{L.save}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
